import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ShieldCheck } from "lucide-react";

export default function ChangePassword() {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const forced = !!user?.must_change_password;

  // Live complexity checks
  const checks = [
    { label: "Almeno 8 caratteri", ok: next.length >= 8 },
    { label: "Una lettera maiuscola", ok: /[A-Z]/.test(next) },
    { label: "Un numero", ok: /\d/.test(next) },
  ];
  const allOk = checks.every((c) => c.ok);

  async function submit(e) {
    e.preventDefault();
    setErr("");
    if (!allOk) { setErr("La password non rispetta i requisiti"); return; }
    if (next !== confirm) { setErr("Le password non corrispondono"); return; }
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      await refresh();
      nav(user?.role === "business" ? "/business" : user?.role === "admin" ? "/admin" : "/me");
    } catch (ex) {
      setErr(formatErr(ex.response?.data?.detail) || ex.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="max-w-md mx-auto px-4 py-16">
      <div className="inline-flex h-11 w-11 rounded-2xl bg-blue-600 text-white items-center justify-center mb-4">
        <ShieldCheck className="h-5 w-5" />
      </div>
      <h1 className="font-display text-3xl font-medium tracking-tight">
        {forced ? "Imposta la tua nuova password" : "Cambia password"}
      </h1>
      <p className="text-zinc-500 mt-2 dark:text-zinc-400">
        {forced
          ? "Per proteggere il tuo account, devi scegliere una password personale al primo accesso."
          : "Aggiorna la tua password in qualsiasi momento."}
      </p>

      <form onSubmit={submit} className="card-soft p-6 mt-8 space-y-4" data-testid="change-pw-form">
        <div>
          <label className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">
            {forced ? "Password temporanea ricevuta" : "Password attuale"}
          </label>
          <input type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} className="input-soft mt-2" data-testid="change-pw-current" />
        </div>
        <div>
          <label className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Nuova password</label>
          <input type="password" required minLength={8} value={next} onChange={(e) => setNext(e.target.value)} className="input-soft mt-2" data-testid="change-pw-new" />
          {next && (
            <ul className="mt-3 space-y-1.5" data-testid="change-pw-checks">
              {checks.map((c) => (
                <li key={c.label} className={`flex items-center gap-2 text-xs ${c.ok ? "text-emerald-600" : "text-zinc-500"}`}>
                  <span className={`inline-flex items-center justify-center h-4 w-4 rounded-full text-[10px] ${c.ok ? "bg-emerald-100 dark:bg-emerald-950/40" : "bg-zinc-100"}`}>
                    {c.ok ? "✓" : "•"}
                  </span>
                  {c.label}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <label className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Conferma nuova password</label>
          <input type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="input-soft mt-2" data-testid="change-pw-confirm" />
        </div>
        {err && <div className="text-sm text-red-600" data-testid="change-pw-error">{err}</div>}
        <button disabled={loading || !allOk || next !== confirm} className="btn-accent w-full" data-testid="change-pw-submit">
          {loading ? "Aggiornamento…" : "Aggiorna password"}
        </button>
        {forced && (
          <button type="button" onClick={async () => { await logout(); nav("/login"); }} className="text-xs text-zinc-500 w-full dark:text-zinc-400">
            Esci
          </button>
        )}
      </form>
    </div>
  );
}
